// One invocation: what was sent (the system prompt and the user prompt as
// sent, capped at write time except for a call of an answer), what came
// back, the thinking, what it cost, and — when it failed — what the provider
// said. The system prompt and the thinking were added 2026-09-27: every
// call's system prompt has been kept since 2026-09-25 (`architecture/0029`
// §3), and this page did not show it.
import type { LlmRunDetail } from "@gb/admin-contracts";
import { ScrollText } from "lucide-react";
import { Link, useParams } from "react-router";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { useRun } from "./runs-api";

export function RunDetailPage() {
  const { tenantSlug = "", runId = "" } = useParams();
  const run = useRun(tenantSlug, runId);

  if (run.isError) return <EmptyState icon={ScrollText} title="Run not found" />;
  if (!run.data) return null;
  const r = run.data;

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to="/runs" className="text-brand hover:underline">
          ← Runs
        </Link>
      </div>
      <PageHeader eyebrow={r.tenantSlug} title={r.touchpoint} />
      <div className="mt-1 font-mono text-[0.75rem] text-faint">
        {r.provider} · {r.model} · prompt v{r.promptVersion} · {r.status} ·{" "}
        {new Date(r.createdAt).toLocaleString()}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
        <Fact label="Latency" value={`${r.latencyMs}ms`} />
        <Fact label="Input tokens" value={r.inputTokens ?? "—"} />
        <Fact label="Output tokens" value={r.outputTokens ?? "—"} />
        <Fact
          label="Cache read / write"
          value={`${r.cacheReadTokens ?? "—"} / ${r.cacheWriteTokens ?? "—"}`}
        />
        <Fact label="Images sent" value={r.imageCount ?? "—"} />
      </div>

      {r.errorClass && (
        <div className="mt-4 rounded-md border border-danger-bg bg-danger-bg p-3 font-mono text-[0.78rem] text-danger">
          {r.errorClass}
        </div>
      )}

      {r.errorMessage && (
        <Section title="Provider’s message">
          <pre className="overflow-x-auto font-mono text-[0.74rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
            {r.errorMessage}
          </pre>
        </Section>
      )}

      {r.images && r.images.length > 0 && (
        <Section title="Pictures, as stored and as sent">
          <ul className="flex flex-col gap-1 font-mono text-[0.74rem] text-ink-prose">
            {r.images.map((image, index) => (
              <li key={index}>{pictureLine(image)}</li>
            ))}
          </ul>
        </Section>
      )}

      {r.subjectKind && (
        <div className="mt-4 font-mono text-[0.75rem] text-muted">
          subject: {r.subjectKind} {r.subjectId}
          {r.projectId ? ` · project ${r.projectId}` : ""}
        </div>
      )}

      <Section title="System prompt as sent">
        <pre className="overflow-x-auto font-mono text-[0.74rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
          {r.systemText ?? "(not recorded: before 25 September 2026 only the source selector’s was kept)"}
        </pre>
      </Section>

      <Section title="Prompt as sent">
        <pre className="overflow-x-auto font-mono text-[0.74rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
          {r.promptText ?? "(not recorded)"}
        </pre>
      </Section>

      <Section title="Thinking">
        <pre className="overflow-x-auto font-mono text-[0.74rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
          {r.thinking ?? "(none returned)"}
        </pre>
      </Section>

      <Section title="Output">
        <pre className="overflow-x-auto font-mono text-[0.74rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
          {r.output === null ? "(none recorded)" : JSON.stringify(r.output, null, 2)}
        </pre>
      </Section>
    </div>
  );
}

/** "image/jpeg 8116 × 3185 px, 591 KB → image/jpeg 2576 × 1011 px, 312 KB (resized)". */
function pictureLine(image: NonNullable<LlmRunDetail["images"]>[number]): string {
  const side = (picture: { mimeType: string; bytes: number; widthPx: number | null; heightPx: number | null }) =>
    `${picture.mimeType}${picture.widthPx && picture.heightPx ? ` ${picture.widthPx} × ${picture.heightPx} px` : ""}, ${Math.max(1, Math.round(picture.bytes / 1024)).toLocaleString("en-GB")} KB`;
  return image.after
    ? `${side(image.before)} → ${side(image.after)} (${image.action})`
    : `${side(image.before)} (not sent)`;
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border-inner bg-white px-3 py-2">
      <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className="font-mono text-[0.85rem] text-ink">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6">
      <div className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">{title}</div>
      <div className="mt-2 rounded-lg border border-border bg-white p-4">{children}</div>
    </div>
  );
}
